import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useNotification from './useNotification';
import usePostErrorStore from '../store/usePostErrorStore';
import { addSentryBreadcrumb, captureException } from '../utils/sentry';

/**
 * Global hook to listen for polling errors and display notifications
 * Should be used at the layout level to catch errors across the entire app
 */
export const useGlobalPollingErrorListener = () => {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { setPostError } = usePostErrorStore();
  const notifiedRef = useRef(new Set());

  useEffect(() => {
    // Subscribe to query cache mutations to catch errors
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const query = event?.query;
      if (!query || query.state?.status !== 'error') return;

      const error = query.state?.error;
      if (!error) return;

      // Dedupe notifications per query + error code/message
      const key = `${query.queryHash || 'unknown'}:${error.code || 'nocode'}:${
        error.message || 'nomessage'
      }`;
      if (notifiedRef.current.has(key)) return;
      notifiedRef.current.add(key);

      // Send error to Sentry for tracking
      captureException(error, {
        query: {
          queryKey: query.queryKey,
          status: query.state?.status,
        },
      });

      // Add breadcrumb for debugging
      addSentryBreadcrumb(
        `Query error: ${error.message}`,
        {
          queryKey: query.queryKey,
          errorCode: error.code,
          errorMessage: error.message,
        },
        'error'
      );

      // Check for regenerate timeout errors
      if (error?.code === 'REGENERATE_TIMEOUT') {
        // Extract postId from queryKey ['post-details', postId]
        const queryKey = query.queryKey || [];
        if (queryKey[0] === 'post-details' && queryKey[1]) {
          const postId = queryKey[1];
          setPostError(postId, error.message);
        }
        notification.warning(
          error.message || 'Post regeneration is taking longer than expected.',
          10000
        );
        return;
      }
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [queryClient, notification, setPostError]);
};

export default useGlobalPollingErrorListener;
